import { useState } from 'react';
import SEO from '../../../components/SEO';

export default function FinancingGuidePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://buyinzagreb.com';

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Mali vodič financiranja nekretnine kreditom",
    "description": "Kompletni vodič kroz proces dobivanja stambenog kredita u Hrvatskoj - od pripreme dokumentacije do potpisa ugovora s bankom.",
    "image": "https://public.readdy.ai/ai/img_res/8b8018a7e6a4fc17349364a8474ca376.jpg",
    "author": {
      "@type": "Organization",
      "name": "BuyInZagreb Tim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BuyInZagreb",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/financiranje-nekretnine-zagreb`
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/blog-financing-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Vodič kroz financiranje nekretnine Zagreb | Stambeni kredit 2024"
        description="Kompletni vodič kroz proces dobivanja stambenog kredita u Hrvatskoj. Dokumentacija, banke, troškovi i savjeti za uspješno financiranje nekretnine Zagreb."
        keywords="stambeni kredit Zagreb, financiranje nekretnine, kredit za stan, banke Zagreb, agencije za nekretnine Zagreb, kupnja nekretnine"
        canonical={`${siteUrl}/blog/financiranje-nekretnine-zagreb`}
        schemaData={articleSchema}
      />

      {/* Header - Consistent with main page */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <a href="/">
                <img 
                  src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                  alt="BuyInZagreb Logo" 
                  className="h-10 w-auto"
                />
              </a>
            </div>
            
            <nav className="hidden lg:flex items-center space-x-6">
              {[
                { name: 'Početna', href: '/' },
                { name: 'Nekretnine', href: '/#listings' },
                { name: 'Usluge', href: '/#services' },
                { name: 'O nama', href: '/#about' },
                { name: 'Blog', href: '/#blog-tools' },
                { name: 'Kontakt', href: '/#contact' }
              ].map((item) => (
                <a 
                  key={item.name}
                  href={item.href} 
                  className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <div className="flex items-center space-x-3">
              <a 
                href="/#contact"
                className="hidden lg:flex bg-red-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Kontaktirajte nas
              </a>
              <button 
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <nav className="px-4 py-4 space-y-3">
              <a 
                href="/" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Početna
              </a>
              <a 
                href="/#listings" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Nekretnine
              </a>
              <a 
                href="/#services" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Usluge
              </a>
              <a 
                href="/#about" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                O nama
              </a>
              <a 
                href="/#blog-tools" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Blog
              </a>
              <a 
                href="/#contact" 
                className="block w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors text-center"
              >
                Kontaktirajte nas
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-16">
        {/* Article Header */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Mali vodič financiranja nekretnine kreditom
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Kompletni vodič kroz proces dobivanja stambenog kredita u Hrvatskoj - od pripreme dokumentacije do potpisa ugovora s bankom.
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span><i className="ri-calendar-line mr-2"></i>15. siječnja 2024.</span>
                <span><i className="ri-time-line mr-2"></i>8 min čitanja</span>
                <span><i className="ri-user-line mr-2"></i>BuyInZagreb Tim</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl mb-12">
              <img 
                src="https://readdy.ai/api/search-image?query=Home%20financing%20mortgage%20consultation%20Zagreb%20Croatia%2C%20financial%20planning%20documents%2C%20calculator%20and%20contracts%2C%20professional%20banking%20environment%2C%20modern%20office%20setting%2C%20realistic%20photography&width=1200&height=675&seq=blog-finance-main&orientation=landscape"
                alt="Financiranje nekretnine Zagreb"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Zašto je stambeni kredit ključan za kupnju nekretnine?</h2>
              
              <p className="text-gray-700 leading-relaxed mb-8">
                Kupnja nekretnine u Zagrebu predstavlja značajnu financijsku investiciju koja često nadilazi mogućnosti gotovinske kupnje. Stambeni kredit omogućuje realizaciju vašeg sna o vlastitom domu uz raspoređivanje financijskog tereta kroz duži vremenski period. Agencije za nekretnine Zagreb rade s licenciranim kreditnim posrednicima koji vam mogu osigurati najbolje uvjete financiranja.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Trenutno stanje na tržištu nekretnina Zagreb pokazuje stabilne cijene s blagim rastom, što čini sada povoljnim trenutkom za kupnju. Kamatne stope na stambene kredite kreću se između 3,5% i 5,5% godišnje, ovisno o banci i uvjetima kreditiranja.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Priprema dokumentacije za stambeni kredit</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Uspješno dobivanje stambenog kredita počinje temeljitom pripremom dokumentacije. Banke zahtijevaju detaljnu provjeru vaše kreditne sposobnosti kroz analizu prihoda, rashoda i postojećih obveza. Agencije za nekretnine s licenciranim kreditnim posrednicima mogu značajno ubrzati ovaj proces.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Potrebna dokumentacija za stambeni kredit:</h3>
                <ul className="space-y-3">
                  {[
                    'Potvrda o primanjima (posljednjih 6 mjeseci)',
                    'Izvadak iz registra zaposlenih ili ugovor o radu',
                    'Izvještaj o kreditnoj sposobnosti (HROK)',
                    'Izjava o imovnom stanju',
                    'Kupoprodajni predugovor ili rezervacija nekretnine',
                    'Procjena vrijednosti nekretnine od ovlaštenog procjenitelja'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Koje banke nude najbolje uvjete za stanovi Zagreb?</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Hrvatska bankarska scena nudi raznolike opcije stambenog kreditiranja. Vodeće banke poput Zagrebačke banke, PBZ-a, Erste banke i Raiffeisen banke imaju specifične proizvode prilagođene kupnji nekretnina Zagreb. Licencirani kreditni posrednici mogu usporediti ponude i pronaći najbolje uvjete za vašu situaciju.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Trenutno najkonkurentniji uvjeti uključuju kamate od 3,5% do 4,2% za kredite u eurima, mogućnost financiranja do 80% vrijednosti nekretnine, i rokove otplate do 30 godina. Mnoge banke nude posebne pogodnosti za mlade obitelji i prva stambena rješenja.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Troškovi povezani s kreditom i kupnjom nekretnine</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Uz glavnicu kredita, potrebno je planirati dodatne troškove koji mogu činiti 5-8% ukupne cijene nekretnine. Procjena vrijednosti nekretnine košta između 2.000 i 4.000 kuna, ovisno o veličini i lokaciji. Agencije za nekretnine često imaju dogovore s procjeniteljima za povoljnije cijene.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-red-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-red-600 mb-4">Bankovski troškovi</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Naknada za obradu zahtjeva: 0,5-1% glavnice</li>
                    <li>• Procjena nekretnine: 2.000-4.000 kn</li>
                    <li>• Osiguranje nekretnine: 0,1-0,2% godišnje</li>
                    <li>• Osiguranje života: 0,3-0,5% glavnice godišnje</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-blue-600 mb-4">Ostali troškovi</h4>
                  <ul className="space-y-2 text-sm">
                    <li>• Javni bilježnik: 800-1.500 €</li>
                    <li>• Porez na promet nekretnina: 3%</li>
                    <li>• Upis u zemljišne knjige: 200-500 kn</li>
                    <li>• Agencijska provizija: 2-3%</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kako povećati šanse za odobrenje kredita?</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Banke procjenjuju kreditnu sposobnost kroz omjer mjesečnih rata prema neto primanjima, koji ne smije prelaziti 30-40%. Stabilno zaposlenje u trajnom radnom odnosu značajno povećava šanse za odobrenje. Dodatni prihodi poput honorara ili najma također se mogu uvrstiti u kalkulaciju.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Veća početna uplata (više od 20%) smanjuje rizik za banku i može rezultirati boljim uvjetima kreditiranja. Suručnici s dobrim kreditnim rejtingom također povećavaju šanse za odobrenje i mogu donijeti povoljniju kamatu.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Postupak odobrenja i isplate kredita</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Nakon podnošenja kompletne dokumentacije, banka provodi detaljnu analizu kreditne sposobnosti što traje 7-15 radnih dana. Ukoliko je zahtjev odobren, slijedi potpis kreditnog ugovora kod javnog bilježnika i upis hipoteke u zemljišne knjige.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Isplata kredita obično se odvija u roku od 15-30 dana nakon potpisa ugovora, direktno na račun prodavatelja nekretnine. Agencije za nekretnine koordiniraju sve dionike u procesu kako bi osigurale glatko odvijanje transakcije.
              </p>

              <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Važne napomene za kupce nekretnina Zagreb:</h3>
                <ul className="space-y-3">
                  {[
                    'Nikada ne potpisujte kupoprodajni ugovor prije odobrenja kredita',
                    'Rezervirajte nekretninu s uvjetom dobivanja financiranja',
                    'Tražite pisanu potvrdu o preliminarnom odobrenju kredita',
                    'Usporedite ponude više banaka prije donošenja odluke'
                  ].map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-error-warning-line text-red-600 mr-3 mt-1"></i>
                      <span className="text-gray-700 font-medium">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Alternativni načini financiranja</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Osim klasičnih stambenih kredita, postoje i alternativni načini financiranja kupnje nekretnine. Stambeno štedni ugovori omogućuju kombinaciju štednje i kredita uz povoljne uvjete. APN (Agencija za pravni promet nekretnina) subvencionira kupnju prve nekretnine za mlade obitelji.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Leasing nekretnina postaje sve popularniji oblik financiranja, posebno za poslovne prostore i investicijske nekretnine. Ovaj oblik financiranja omogućuje korištenje nekretnine uz mjesečne rate, s opcijom kupnje na kraju ugovora.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Savjeti za uspješno dobivanje kredita</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Pripremite se temeljito prije odlaska u banku. Uredite sve postojeće kredite i kartice, zatvorite nepotrebne račune i pobrinite se da vaš HROK izvještaj bude čist. Agencije za nekretnine s kreditnim stručnjacima mogu vam pomoći u pripremi dokumentacije i strategiji pristupa bankama.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Realno procjenjujte svoju financijsku situaciju i ne preuzimajte više nego što možete podnijeti. Ostavite rezervu za nepredviđene troškove i održavanje nekretnine. Kvalitetno planiranje financiranja temelj je sigurne kupnje nekretnine u Zagrebu.
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-gray-50 rounded-3xl p-12 text-center mt-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Trebate pomoć oko financiranja nekretnine?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Naš tim licenciranih kreditnih stručnjaka pomoći će vam pronaći najbolje uvjete financiranja za kupnju nekretnine Zagreb. Analizirat ćemo vašu financijsku situaciju i pripremiti strategiju za uspješno dobivanje kredita.
              </p>
              
              <form 
                onSubmit={handleSubmit}
                className="max-w-md mx-auto space-y-4 mb-8"
                data-readdy-form="blog-financing-guide"
              >
                <input 
                  type="text"
                  name="name"
                  placeholder="Vaše ime i prezime"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <input 
                  type="email"
                  name="email"
                  placeholder="Email adresa"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <input 
                  type="tel"
                  name="phone"
                  placeholder="Broj telefona"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <textarea 
                  name="message"
                  placeholder="Ukratko opišite svoju situaciju i potrebe..."
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors h-32 resize-none"
                  required
                ></textarea>
                
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-red-600 text-white py-4 rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Šalje se...' : 'Zatraži besplatnu konsultaciju'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl text-center">
                    Hvala vam! Kontaktirat ćemo vas u najkraćem mogućem roku.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-center">
                    Dogodila se greška. Molimo pokušajte ponovo.
                  </div>
                )}
              </form>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-calculator-line mr-3"></i>
                  Kalkulator troškova
                </button>
                <a 
                  href="/"
                  className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-arrow-left-line mr-3"></i>
                  Povratak na početnu
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      {/* Footer - Simplified */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4">BuyInZagreb</h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Vodeća agencija za nekretnine u Zagrebu s više od 10 godina iskustva u prodaji stanova i kuća.
          </p>
          <div className="flex justify-center items-center gap-6">
            <a href="/#contact" className="text-gray-300 hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}